import { useState } from 'react';
import {
  apiErrorText,
  useListFsnbEditionChangesQuery,
  type FsnbEdition,
  type FsnbEditionChange,
} from '@shared/api';
import { fmtNum } from '@shared/lib/smetaLabels';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { ResourceCardDrawer, WorkCardDrawer } from '../../normBaseCards/index';
import {
  BASE_TYPES,
  changeLabel,
  changeTone,
  changedFieldLabel,
  fullCode,
} from '../../normBaseLabels';
import {
  Alert,
  CHANGES_PAGE,
  Chip,
  FilterInput,
  FilterSelect,
  Grid,
  Grow,
  Muted,
  Note,
  Row,
  TableWrap,
} from '../styles';

/** Список изменений редакции против базовой. */
export function ChangesList({ edition }: { edition: FsnbEdition }) {
  const [entity, setEntity] = useState<'work' | 'resource'>('work');
  const [change, setChange] = useState('');
  const [kind, setKind] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<FsnbEditionChange | null>(null);
  const q = useDebouncedValue(query.trim(), 300);
  const res = useListFsnbEditionChangesQuery({
    id: edition.id,
    entity,
    change: change || undefined,
    kind: entity === 'work' ? kind || undefined : undefined,
    q: q || undefined,
    limit: CHANGES_PAGE,
    offset: page * CHANGES_PAGE,
  });
  const base = edition.diff?.base_edition ?? null;
  const totalFound = res.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(totalFound / CHANGES_PAGE));

  return (
    <>
      <Row>
        <Chip
          type="button"
          $active={entity === 'work'}
          onClick={() => {
            setEntity('work');
            setPage(0);
          }}
        >
          Нормы
        </Chip>
        <Chip
          type="button"
          $active={entity === 'resource'}
          onClick={() => {
            setEntity('resource');
            setKind('');
            setPage(0);
          }}
        >
          Ресурсы ФСБЦ
        </Chip>
        <FilterSelect
          value={change}
          aria-label="Вид изменения"
          onChange={(e) => {
            setChange(e.target.value);
            setPage(0);
          }}
        >
          <option value="">Все изменения</option>
          <option value="added">Новые</option>
          <option value="changed">Изменённые</option>
          <option value="removed">Исключённые</option>
        </FilterSelect>
        {entity === 'work' && (
          <FilterSelect
            value={kind}
            aria-label="Вид базы"
            onChange={(e) => {
              setKind(e.target.value);
              setPage(0);
            }}
          >
            <option value="">Все виды базы</option>
            {BASE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </FilterSelect>
        )}
        <FilterInput
          type="search"
          value={query}
          placeholder="Шифр или слова наименования"
          aria-label="Поиск по изменениям"
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
      </Row>
      {res.error && (
        <Alert $tone="danger">{apiErrorText(res.error, 'Изменения не загрузились.')}</Alert>
      )}
      {res.data && res.data.items.length === 0 && <Note>Изменений с такими условиями нет.</Note>}
      {res.data && res.data.items.length > 0 && (
        <TableWrap>
          <Grid>
            <thead>
              <tr>
                <th>Что</th>
                <th>Шифр</th>
                <th>Наименование</th>
                <th>Что изменилось</th>
              </tr>
            </thead>
            <tbody>
              {res.data.items.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => setOpen(c)}>
                  <td>
                    <Badge $tone={changeTone(c.change)}>{changeLabel(c)}</Badge>
                  </td>
                  <td className="code">
                    {c.entity === 'work' ? fullCode(c.kind, c.code) : c.code}
                  </td>
                  <td>{c.name ?? '—'}</td>
                  <td>{c.fields.map(changedFieldLabel).join(', ') || <Muted>—</Muted>}</td>
                </tr>
              ))}
            </tbody>
          </Grid>
        </TableWrap>
      )}
      {totalFound > 0 && (
        <Row>
          <Muted>Всего: {fmtNum(totalFound)}</Muted>
          <Grow />
          {pages > 1 && (
            <>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Назад
              </Button>
              <Muted>
                {page + 1} из {fmtNum(pages)}
              </Muted>
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Дальше
              </Button>
            </>
          )}
        </Row>
      )}
      {open && open.entity === 'work' && (
        <WorkCardDrawer
          baseType={open.kind}
          code={open.code}
          // Исключённой нормы в новой редакции нет — открываем её в прежней.
          edition={open.change === 'removed' && base ? base : edition.edition}
          editionTitle={open.change === 'removed' ? 'исключена в новой редакции' : edition.title}
          compareEdition={open.change === 'changed' ? base : null}
          onClose={() => setOpen(null)}
        />
      )}
      {open && open.entity === 'resource' && (
        <ResourceCardDrawer
          source={open.kind}
          code={open.code}
          edition={open.change === 'removed' && base ? base : edition.edition}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}
